import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { resendVerification } from '../app/redux/slices/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import validator from 'validator';
import { AppDispatch } from '../app/redux/store';
import LanguageSwitcher from '../app/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

export default function ContinueVerificationPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validEmail, setValidEmail] = useState(true);
  const [passwordError, setPasswordError] = useState(false);
  const [fakeSubmitting, setFakeSubmitting] = useState(false);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Email validation
  useEffect(() => {
    if (email) {
      if (validator.isEmail(email)) {
        setValidEmail(true);
      } else {
        setValidEmail(false);
      }
    } else {
      setValidEmail(true);
    }
  }, [email]);

  // Password validation - at least 8 characters
  useEffect(() => {
    if (password) {
      const reg = /.{8,}$/;
      if (!reg.test(password)) {
        setPasswordError(true);
      } else {
        setPasswordError(false);
      }
    } else {
      setPasswordError(false);
    }
  }, [password]);

  const handleContinueVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !validEmail || !password || passwordError) {
      setFakeSubmitting(true);
      return;
    }

    setFakeSubmitting(false);

    try {
      await dispatch(resendVerification(email)).unwrap();
      // Navigate to verification page with email and password
      navigate(`/verification?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`);
    } catch (error) {
      logger.error('Failed to resend verification:', error);
    }
  };

  const goBack = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('continueVerificationTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900">
            {t('resendVerificationDescription')}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleContinueVerification}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                {t('emailAddress')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('enterEmailAddress')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {!email && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('emailRequired')}</p>
              )}
              {!validEmail && (
                <p className="mt-1 text-sm text-red-600">{t('validEmailRequired')}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                {t('password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {!password && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('passwordRequired')}</p>
              )}
              {passwordError && (
                <p className="mt-1 text-sm text-red-600">{t('passwordMinLength')}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('continueVerificationTitle')}
            </button>
          </div>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={goBack}
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('backToRegister')}
            </button>
            <br />
            <Link 
              to="/login" 
              className="font-medium text-gray-900 hover:text-gray-800 text-sm"
            >
              {t('alreadyVerified')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
